import { useEffect, useState } from 'react';
import { PRODUCT_PRICE_DEFAULT_UNIT } from '../constants/maintenance.constants';
import {
	formatPriceInput,
	fromPriceUnitValue,
	parsePriceInput,
	productKey,
	toPriceUnitValue,
	type PackProduct,
	type ProductCostFormEntry,
	type ProductPriceMap,
} from '../utils/productCosts';

interface UseProductCostsFormOptions {
	isOpen: boolean;
	products: PackProduct[];
	prices: ProductPriceMap;
	onSave: (prices: ProductPriceMap) => void;
}

const buildEntries = (products: PackProduct[], prices: ProductPriceMap) =>
	Object.fromEntries(
		products.map((product) => {
			const price = prices[productKey(product.id)];
			return [
				product.id,
				{
					price: price ? formatPriceInput(price.price) : '',
					unit: price ? toPriceUnitValue(price) : PRODUCT_PRICE_DEFAULT_UNIT,
				},
			];
		})
	) as Record<string, ProductCostFormEntry>;

export const useProductCostsForm = ({ isOpen, products, prices, onSave }: UseProductCostsFormOptions) => {
	const [entries, setEntries] = useState<Record<string, ProductCostFormEntry>>(() =>
		buildEntries(products, prices)
	);
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (!isOpen) return;
		setEntries(buildEntries(products, prices));
		setErrors({});
	}, [isOpen, products, prices]);

	const set = (id: string, key: keyof ProductCostFormEntry, value: string) => {
		setEntries((current) => ({ ...current, [id]: { ...current[id], [key]: value } }));
		setErrors((current) => ({ ...current, [id]: '' }));
	};

	const handleSubmit = () => {
		const nextErrors: Record<string, string> = {};
		const nextPrices: ProductPriceMap = {};

		products.forEach((product) => {
			const entry = entries[product.id];
			const price = parsePriceInput(entry?.price ?? '');
			if (price == null) return;
			if (Number.isNaN(price)) {
				nextErrors[product.id] = 'Preço inválido';
				return;
			}
			nextPrices[productKey(product.id)] = {
				id: product.id,
				price,
				...fromPriceUnitValue(entry.unit),
			};
		});

		if (Object.keys(nextErrors).length) {
			setErrors(nextErrors);
			return;
		}

		onSave(nextPrices);
	};

	return { entries, errors, set, handleSubmit };
};
